import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

/**
 * Mini floating "back to top" button that pins to the bottom-right of a
 * scrollable sidebar `<nav>`. Only visible once the user has scrolled
 * ~100 px inside the nav; smooth-scrolls the nav back to top on click.
 *
 * Props:
 *   targetRef   React.RefObject<HTMLElement> — the scrollable nav element
 *   collapsed   boolean — hides the button in collapsed-icon mode where
 *               there's no room for it
 */
export default function SidebarBackToTop({ targetRef, collapsed }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = targetRef?.current;
    if (!el) return undefined;
    const onScroll = () => setVisible(el.scrollTop > 100);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    // Also react to viewport resize since the overflow state can change.
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [targetRef]);

  if (collapsed || !visible) return null;

  const scrollToTop = () => {
    const el = targetRef?.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      data-testid="sidebar-back-to-top"
      onClick={scrollToTop}
      title="Back to top"
      className="absolute right-2 bottom-2 z-10 size-7 flex items-center justify-center border border-[#27272A] bg-[#0A0A0A]/95 backdrop-blur-sm text-zinc-500 hover:text-[#FF3B30] hover:border-[#FF3B30] transition-colors duration-150 shadow-lg"
    >
      <ArrowUp className="size-3.5" strokeWidth={2.5} />
    </button>
  );
}
